import { createApi } from '@reduxjs/toolkit/query/react';
import { type ReferentPriceType } from '@strata/constants';
import { type ReferralStatusIndicator } from '@strata/react-status-indicators';

import { CLIENT_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface ReferralDetails {
    referralId: string;
    payerId: string;
    productServiceOid: string;
    product: string;
    isBrand: boolean;
    diagnosisOid: string;
    winningBidRate?: string;
    referentPriceType?: ReferentPriceType;
    referralStatus: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
    placeOfService: string;
    routeName?: string;
    auctionEnd?: string;
    referralStart?: string;
    referralExpiration: string;
    priceSource?: string;
    priceIsFrozen?: boolean;
    friendlyId: string;
}

export interface ReferralAuth {
    caseId: string;
    authStart: string;
    authExpiration: string;
    billingProvider?: string;
    servicingProvider?: string;
    requestingProvider?: string;
    servicingFacility?: string;
}

export const clientApi = createApi({
    baseQuery: getBaseQuery(CLIENT_API_URL),
    reducerPath: 'clientApi',
    endpoints: (build) => ({
        getReferralDetails: build.query<ReferralDetails, { payerId: string; referralId: string }>({
            query: ({ payerId, referralId }) => `admin/${payerId}/referrals/${referralId}/display`,
            transformResponse: (response: {
                referral_id: string;
                payer_id: string;
                product_service_oid: string;
                product_service_display_name: string;
                is_brand: boolean;
                dx_oid: string;
                winningbid_rate?: string;
                referent_price_type?: ReferentPriceType;
                referral_status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
                place_of_service: string;
                route_name?: string;
                auction_end?: string;
                referral_start?: string;
                referral_expiration: string;
                price_source?: string;
                price_is_frozen?: boolean;
                friendly_id: string;
            }) => ({
                referralId: response?.referral_id,
                payerId: response?.payer_id,
                productServiceOid: response?.product_service_oid,
                product: response?.product_service_display_name,
                isBrand: response?.is_brand,
                diagnosisOid: response?.dx_oid,
                winningBidRate: response?.winningbid_rate,
                referentPriceType: response?.referent_price_type,
                referralStatus:
                    response?.referral_status?.trim() as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses],
                placeOfService: response?.place_of_service?.trim(),
                routeName: response?.route_name,
                auctionEnd: response?.auction_end,
                referralStart: response?.referral_start,
                referralExpiration: response?.referral_expiration,
                priceSource: response?.price_source,
                priceIsFrozen: response?.price_is_frozen,
                friendlyId: response?.friendly_id,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while fetching referral details.',
            ),
        }),
        getAdditionalReferralDetails: build.query<
            {
                referralId: string;
                payerId: string;
                defaultRate?: string;
                referentPriceType?: ReferentPriceType;
            },
            { payerId: string; referralId: string }
        >({
            query: ({ payerId, referralId }) => `admin/${payerId}/referrals/${referralId}`,
            transformResponse: (response: {
                referral_id: string;
                payer_id: string;
                default_rate?: string;
                referent_price_type?: ReferentPriceType;
            }) => ({
                referralId: response?.referral_id,
                payerId: response?.payer_id,
                defaultRate: response?.default_rate,
                referentPriceType: response?.referent_price_type,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while fetching referral details.',
            ),
        }),
        /** Get auths associated with a referral */
        getReferralAuths: build.query<ReferralAuth[], { payerId: string; referralId: string }>({
            query: ({ payerId, referralId }) => `admin/${payerId}/auths/${referralId}`,
            transformResponse: (
                response: {
                    case_id: string;
                    auth_start: string;
                    auth_expiration: string;
                    billing_provider?: string;
                    servicing_provider?: string;
                    requesting_provider?: string;
                    servicing_facility?: string;
                }[],
            ) =>
                response?.map((auth) => ({
                    caseId: auth.case_id,
                    authStart: auth.auth_start,
                    authExpiration: auth.auth_expiration,
                    billingProvider: auth.billing_provider,
                    servicingProvider: auth.servicing_provider,
                    requestingProvider: auth.requesting_provider,
                    servicingFacility: auth.servicing_facility,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while fetching referral auths.',
            ),
        }),
    }),
});

export const {
    useGetReferralDetailsQuery,
    useGetAdditionalReferralDetailsQuery,
    useGetReferralAuthsQuery,
} = clientApi;
